import { isEmpty } from 'lodash';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '../../../../components/Link';
import { Thumbnail } from '../../../../components/thumbnail/thumbnail';
import { SvgLock, SvgPlayCircle, SvgSchedule } from '../../../../icons';
import { EpisodeAsset } from '../../../../queries/ats/titles/types/asset';
import { ProgramSource } from '../../../../queries/ats/titles/types/sources';
import { getPreferredRecording } from '../../../../queries/ats/titles/utils/recording-utils';
import { getLiveProgramProgress } from '../../../../queries/playbills/utils';
import { routes } from '../../../../routes/route-utils';
import { useAuth } from '../../../../utils/auth-utils';
import { getPercent } from '../../../../utils/commonUtils';
import { getTimeStatus, isLimitedToHomeNetwork } from '../../details-call-to-action/utils';
import { useDetailsImage } from '../../use-details-image';
import { EpisodeThumbnailLogIn } from './episode-thumbnail-log-in';
import { RecordedProgramThumbnail } from './recorded-program-thumbnail';

interface EpisodeProgramThumbnailProps {
  episode: EpisodeAsset;
  source: ProgramSource;
}

export function EpisodeProgramThumbnail({ episode, source }: EpisodeProgramThumbnailProps) {
  const now = moment();
  const { t } = useTranslation();
  const startTime = moment(source.startTime);
  const expiresAt = moment(source.expiresAt);
  const status = getTimeStatus(source);
  const { loggedInWithCredentials, loggedInWithIp, isHome } = useAuth();
  const isAuth = Boolean(loggedInWithCredentials || loggedInWithIp);
  const canPlayLive = source.isSubscribed && status === 'present';
  const canWatchFromArchive = source.isSubscribed && status === 'past' && !isEmpty(source.reruns);

  const isExpired = now.isAfter(expiresAt);

  const recording = getPreferredRecording(source, true);

  const src = useDetailsImage({ asset: episode, shape: 'wide', width: 200 });

  const image = {
    src: src,
    alt: `${episode.titleName} cover`,
  };

  const progress =
    source.bookmark !== undefined && episode.duration
      ? getPercent(source.bookmark.position, episode.duration)
      : undefined;

  if (recording !== undefined && recording.status === 'completed') {
    return <RecordedProgramThumbnail episode={episode} recording={recording} />;
  }

  if (isLimitedToHomeNetwork(source) && !isHome) {
    return (
      <Thumbnail
        image={image}
        icon={SvgLock}
        iconText={t('permission only on homenet')}
        interactions={{
          backdrop: 'always',
          icon: 'always',
        }}
      />
    );
  }

  if (canPlayLive) {
    const playLink = routes.live({ channelId: source.id });
    const liveProgress = getLiveProgramProgress(source.startTime, source.endTime);

    return (
      <Link to={playLink}>
        <Thumbnail
          image={image}
          icon={SvgPlayCircle}
          progress={liveProgress}
          interactions={{
            backdrop: 'hover',
            icon: 'hover',
          }}
        />
      </Link>
    );
  }

  if (canWatchFromArchive) {
    const rerun = source.reruns[0];
    const playLink = routes.play({
      platformTitleId: rerun.platformTitleId,
      providerIdentifier: rerun.providerIdentifier,
    });
    const liveProgress = getLiveProgramProgress(source.startTime, source.endTime);

    return (
      <Link to={playLink}>
        <Thumbnail
          image={image}
          icon={SvgPlayCircle}
          progress={liveProgress || progress}
          interactions={{
            backdrop: 'hover',
            icon: 'hover',
          }}
        />
      </Link>
    );
  }

  if (startTime.isAfter(now, 'minutes')) {
    return (
      <Thumbnail
        image={image}
        icon={SvgSchedule}
        progress={progress}
        interactions={{
          backdrop: 'always',
          icon: 'always',
        }}
      />
    );
  }

  if (!isAuth) {
    return <EpisodeThumbnailLogIn image={image} progress={progress} />;
  }

  return (
    <Thumbnail
      image={image}
      icon={null}
      progress={progress}
      interactions={{
        backdrop: isExpired ? 'always' : 'never',
        icon: 'never',
      }}
    />
  );
}
